import type { UserRecord } from "@yahshi/contracts";

export type OverviewPeriod = "week" | "month" | "quarter" | "year";

const SHANGHAI = "Asia/Shanghai";

const periodMeta: Record<OverviewPeriod, { label: string; rangeLabel: string; previousLabel: string }> = {
  week: { label: "近七天", rangeLabel: "近 7 天", previousLabel: "较前 7 天" },
  month: { label: "近五周", rangeLabel: "近 5 周", previousLabel: "较前 5 周" },
  quarter: { label: "近三月", rangeLabel: "近 3 个月", previousLabel: "较前 3 个月" },
  year: { label: "近半年", rangeLabel: "近 6 个月", previousLabel: "较前 6 个月" },
};

function pad(value: number) {
  return String(value).padStart(2, "0");
}

export function shanghaiDayKey(value: Date | string, timeZone = SHANGHAI): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(value));
}

export function shiftDayKey(dayKey: string, deltaDays: number): string {
  const [year, month, day] = dayKey.split("-").map(Number);
  const shifted = new Date(Date.UTC(year!, month! - 1, day! + deltaDays));
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}

export function dayDiff(laterKey: string, earlierKey: string): number {
  const [y1, m1, d1] = laterKey.split("-").map(Number);
  const [y2, m2, d2] = earlierKey.split("-").map(Number);
  return Math.round((Date.UTC(y1!, m1! - 1, d1!) - Date.UTC(y2!, m2! - 1, d2!)) / 86_400_000);
}

function weekdayLabel(dayKey: string, timeZone = SHANGHAI): string {
  const [year, month, day] = dayKey.split("-").map(Number);
  const utc = Date.UTC(year!, month! - 1, day!, 4, 0, 0);
  return new Intl.DateTimeFormat("zh-CN", { timeZone, weekday: "short" }).format(new Date(utc));
}

function monthKey(dayKey: string): string {
  return dayKey.slice(0, 7);
}

function shiftMonthKey(key: string, deltaMonths: number): string {
  const [year, month] = key.split("-").map(Number);
  const shifted = new Date(Date.UTC(year!, month! - 1 + deltaMonths, 1));
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}`;
}

function monthLabel(key: string): string {
  return `${Number(key.slice(5))} 月`;
}

function countCreatedOn(users: UserRecord[], predicate: (createdKey: string) => boolean): number {
  return users.filter((user) => predicate(shanghaiDayKey(user.createdAt))).length;
}

export function percentChange(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

export function recentLoginCounts(users: UserRecord[], now = new Date()) {
  const today = shanghaiDayKey(now);
  const within = (days: number) => users.filter((user) => {
    if (!user.lastLoginAt) return false;
    const ago = dayDiff(today, shanghaiDayKey(user.lastLoginAt));
    return new Date(user.lastLoginAt) <= now && ago >= 0 && ago <= days;
  }).length;
  return { day1: within(0), day7: within(6), day30: within(29) };
}

export function lifecycleCounts(users: UserRecord[], now = new Date()) {
  const today = shanghaiDayKey(now);
  const counts = { newUsers: 0, active: 0, silent: 0, churned: 0 };
  for (const user of users) {
    if (new Date(user.createdAt) > now) continue;
    const createdAgo = dayDiff(today, shanghaiDayKey(user.createdAt));
    if (createdAgo <= 6) {
      counts.newUsers += 1;
      continue;
    }
    const loginAgo = user.lastLoginAt && new Date(user.lastLoginAt) <= now ? dayDiff(today, shanghaiDayKey(user.lastLoginAt)) : Number.POSITIVE_INFINITY;
    if (loginAgo <= 6) counts.active += 1;
    else if (loginAgo <= 29) counts.silent += 1;
    else counts.churned += 1;
  }
  return counts;
}

export function growthSeries(users: UserRecord[], period: OverviewPeriod, now = new Date()) {
  users = users.filter(user => new Date(user.createdAt) <= now);
  const today = shanghaiDayKey(now);
  const currentMonth = monthKey(today);

  if (period === "week") {
    const growth = Array.from({ length: 7 }, (_, index) => {
      const key = shiftDayKey(today, index - 6);
      return { label: weekdayLabel(key), value: countCreatedOn(users, (created) => created === key) };
    });
    const currentTotal = growth.reduce((sum, item) => sum + item.value, 0);
    const previousTotal = countCreatedOn(users, (created) => created >= shiftDayKey(today, -13) && created <= shiftDayKey(today, -7));
    return { ...periodMeta.week, growth, currentTotal, previousTotal, deltaPercent: percentChange(currentTotal, previousTotal) };
  }

  if (period === "month") {
    const growth = Array.from({ length: 5 }, (_, week) => {
      const endOffset = (4 - week) * -7;
      const end = shiftDayKey(today, endOffset);
      const start = shiftDayKey(end, -6);
      return {
        label: `第 ${week + 1} 周`,
        value: countCreatedOn(users, (created) => created >= start && created <= end),
      };
    });
    const currentTotal = growth.reduce((sum, item) => sum + item.value, 0);
    const previousEnd = shiftDayKey(today, -35);
    const previousStart = shiftDayKey(previousEnd, -34);
    const previousTotal = countCreatedOn(users, (created) => created >= previousStart && created <= previousEnd);
    return { ...periodMeta.month, growth, currentTotal, previousTotal, deltaPercent: percentChange(currentTotal, previousTotal) };
  }

  if (period === "quarter") {
    const months = [shiftMonthKey(currentMonth, -2), shiftMonthKey(currentMonth, -1), currentMonth];
    const growth = months.map((key, index) => ({
      label: `第 ${index + 1} 月`,
      value: countCreatedOn(users, (created) => monthKey(created) === key),
    }));
    const currentTotal = growth.reduce((sum, item) => sum + item.value, 0);
    const previousMonths = new Set([shiftMonthKey(currentMonth, -5), shiftMonthKey(currentMonth, -4), shiftMonthKey(currentMonth, -3)]);
    const previousTotal = countCreatedOn(users, (created) => previousMonths.has(monthKey(created)));
    return { ...periodMeta.quarter, growth, currentTotal, previousTotal, deltaPercent: percentChange(currentTotal, previousTotal) };
  }

  const months = Array.from({ length: 6 }, (_, index) => shiftMonthKey(currentMonth, index - 5));
  const growth = months.map((key) => ({
    label: monthLabel(key),
    value: countCreatedOn(users, (created) => monthKey(created) === key),
  }));
  const currentTotal = growth.reduce((sum, item) => sum + item.value, 0);
  const previousMonths = new Set(Array.from({ length: 6 }, (_, index) => shiftMonthKey(currentMonth, index - 11)));
  const previousTotal = countCreatedOn(users, (created) => previousMonths.has(monthKey(created)));
  return { ...periodMeta.year, growth, currentTotal, previousTotal, deltaPercent: percentChange(currentTotal, previousTotal) };
}

export function formatDeltaPercent(deltaPercent: number | null, previousLabel: string): { text: string; tone: "up" | "down" | "flat" | "none" } {
  if (deltaPercent === null) return { text: `${previousLabel}暂无基数`, tone: "none" };
  if (deltaPercent > 0) return { text: `${previousLabel} +${deltaPercent}%`, tone: "up" };
  if (deltaPercent < 0) return { text: `${previousLabel} ${deltaPercent}%`, tone: "down" };
  return { text: `${previousLabel}持平`, tone: "flat" };
}
